import { apiGet, apiPut } from "../api";
import Logger from "@/utils/Logger";
import { createErrorHandler } from "@/utils/ApiErrorHandler";
import {
  ApiResponse,
  DefaultThreshold,
  UpdateDefaultThresholdRequest,
  ThresholdHistoryParams,
} from "@/interfaces/RiskThresholdTypes";

// To be able to mock Toast in unit tests
// we need to call getToast dynamically within each method instead of initializing Toast globally.
import { getToast } from "@/hooks/useToast";

/**
 * Error handler for threshold default operations
 */
const handleThresholdError = createErrorHandler("DefaultThresholdApi");

/**
 * API client for Risk Threshold Defaults
 * Manages global threshold defaults that apply to all commodities
 */
export const DefaultThresholdApi = {
  /**
   * Get active threshold defaults
   * @returns Currently active global threshold defaults
   * @throws Error if request fails
   */
  getActiveDefaults: async (): Promise<DefaultThreshold> => {
    try {
      Logger.info("Fetching active threshold defaults", [
        "DefaultThresholdApi",
      ]);

      const response = await apiGet<ApiResponse<DefaultThreshold>>(
        "/risk-thresholds/defaults",
      );

      if (!response || !response.responseObject) {
        throw new Error("Invalid response structure from API");
      }

      const withDefaultCommodityName = (
        d: DefaultThreshold,
      ): DefaultThreshold => ({
        ...d,
        scmCommodityNm: d.scmCommodityNm ?? "Default Threshold",
      });

      const defaults = withDefaultCommodityName(response.responseObject);

      Logger.info("Successfully fetched active threshold defaults", [
        "DefaultThresholdApi",
      ]);

      // Cache in session storage
      try {
        sessionStorage.setItem(
          "activeThresholdDefaults",
          JSON.stringify(defaults),
        );
        Logger.debug("Cached threshold defaults in session storage", [
          "DefaultThresholdApi",
        ]);
      } catch {
        Logger.warn("Failed to cache threshold defaults", [
          "DefaultThresholdApi",
        ]);
      }

      return defaults;
    } catch (error: unknown) {
      const errorInfo = handleThresholdError(error, "getActiveDefaults");
      getToast().error(errorInfo.message);
      throw error;
    }
  },

  /**
   * Update threshold defaults
   * Creates a new version of threshold defaults (deactivates old, inserts new)
   * @param request Update request with new threshold values
   * @returns Updated threshold defaults
   * @throws Error if validation fails or request fails
   */
  updateDefaults: async (
    request: UpdateDefaultThresholdRequest,
  ): Promise<DefaultThreshold> => {
    try {
      Logger.info("Updating threshold defaults", ["DefaultThresholdApi"], {
        request: request as unknown as Record<string, unknown>,
      });

      // Client-side validation
      const validationErrors = validateThresholdRanges(request);
      if (validationErrors.length > 0) {
        const errorMessage = `Validation failed: ${validationErrors.join(", ")}`;
        Logger.error(errorMessage, null, ["DefaultThresholdApi"]);
        throw new Error(errorMessage);
      }

      const response = await apiPut<ApiResponse<DefaultThreshold>>(
        "/risk-thresholds/defaults",
        request,
      );

      if (!response || !response.responseObject) {
        throw new Error("Invalid response structure from API");
      }

      const updatedDefaults = response.responseObject;

      Logger.info("Successfully updated threshold defaults", [
        "DefaultThresholdApi",
      ]);

      return updatedDefaults;
    } catch (error: unknown) {
      const errorInfo = handleThresholdError(error, "updateDefaults");
      Logger.error(errorInfo.message, error, ["DefaultThresholdApi"]);
      throw error;
    } finally {
      // Clear cached defaults to force fresh fetch
      sessionStorage.removeItem("activeThresholdDefaults");
    }
  },

  /**
   * Get threshold defaults history
   * @param params Optional date range filters
   * @returns Array of historical threshold defaults
   * @throws Error if request fails
   */
  getDefaultsHistory: async (
    params?: ThresholdHistoryParams,
  ): Promise<DefaultThreshold[]> => {
    try {
      Logger.info("Fetching threshold defaults history", [
        "DefaultThresholdApi",
      ]);

      const queryParams = new URLSearchParams();
      if (params?.startDate) {
        queryParams.append("startDate", params.startDate);
      }
      if (params?.endDate) {
        queryParams.append("endDate", params.endDate);
      }

      const url = `/risk-thresholds/defaults/history${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      const response = await apiGet<ApiResponse<DefaultThreshold[]>>(url);

      if (!response || !Array.isArray(response.responseObject)) {
        throw new Error("Invalid response structure from API");
      }

      const history = response.responseObject;

      Logger.info(
        `Successfully fetched ${history.length} historical threshold defaults`,
        ["DefaultThresholdApi"],
      );

      return history;
    } catch (error: unknown) {
      const errorInfo = handleThresholdError(error, "getDefaultsHistory");
      getToast().error(errorInfo.message);
      throw error;
    }
  },

  /**
   * Get cached active defaults from session storage
   * @returns Cached defaults or null if not available
   */
  getCachedDefaults: (): DefaultThreshold | null => {
    try {
      const cached = sessionStorage.getItem("activeThresholdDefaults");
      if (cached) {
        Logger.debug("Loading threshold defaults from cache", [
          "DefaultThresholdApi",
        ]);
        return JSON.parse(cached) as DefaultThreshold;
      }
    } catch {
      Logger.warn("Failed to load cached threshold defaults", [
        "DefaultThresholdApi",
      ]);
    }
    return null;
  },
};

/**
 * Validate threshold range values
 * Ensures ranges don't overlap and values are within bounds
 */
export const validateThresholdRanges = (
  request: UpdateDefaultThresholdRequest,
): string[] => {
  const errors: string[] = [];

  // Validate highMax < mediumMin
  if (request.highMax >= request.mediumMin) {
    errors.push("High max must be less than Medium min");
  }

  // Validate mediumMax < lowMin
  if (request.mediumMax >= request.lowMin) {
    errors.push("Medium max must be less than Low min");
  }

  // Validate values are within 0-10 range
  if (request.highMax < 0 || request.highMax > 10) {
    errors.push("High max must be between 0 and 10");
  }
  if (request.mediumMin < 0 || request.mediumMin > 10) {
    errors.push("Medium min must be between 0 and 10");
  }
  if (request.mediumMax < 0 || request.mediumMax > 10) {
    errors.push("Medium max must be between 0 and 10");
  }
  if (request.lowMin < 0 || request.lowMin > 10) {
    errors.push("Low min must be between 0 and 10");
  }

  // Validate userName is provided
  if (!request.userName || request.userName.trim() === "") {
    errors.push("User name is required");
  }

  return errors;
};
